import {useEffect,useRef,useState} from 'react';
import supabase from '../supabase/supabase';
import {reviewRpc} from '../lib/clientReview';

// Existing source-transcript feedback stays in its original private review RPCs.
// No passage text or reviewer email is broadcast.
export default function useTranscriptMarginNotes({projectId,sourceId,clientNotes,clientReplies,onClientReply}){
 const [ownerData,setOwnerData]=useState({notes:[],replies:[]});
 const [busy,setBusy]=useState(false),[error,setError]=useState('');
 const [drafts,setDrafts]=useState({});
 const mounted=useRef(true);
 const ownerCall=(action,payload={})=>reviewRpc(supabase,'cutscript_review_owner',{p_project:projectId,p_action:action,p_payload:payload});
 const replyCall=(action,payload={})=>reviewRpc(supabase,'cutscript_review_workflow_owner',{p_project:projectId,p_action:action,p_payload:payload});
 const storyCall=(action,payload={})=>reviewRpc(supabase,'cutscript_story_comment_owner',{p_project:projectId,p_action:action,p_payload:payload});
 const refresh=async()=>{
  if(!projectId || clientNotes)return;
  try{
   const basic=await ownerCall('list');
   const [detail,story]=await Promise.all([
     basic.review?replyCall('list'):{replies:[]},
     storyCall('list')
   ]);
   const privateNotes=(story.comments||[]).filter(n=>n.block_id==='transcript:'+sourceId).map(n=>({
     ...n,id:n.id,kind:'comment',source_id:sourceId,first_word:n.start_char,last_word:n.end_char-1,
     guest_name:'Editor',private:true
   }));
   const privateReplies=(story.replies||[]).map(r=>({...r,feedback_id:r.comment_id}));
   if(mounted.current)setOwnerData({notes:[...(basic.feedback||[]),...privateNotes],replies:[...(detail.replies||[]),...privateReplies]});
  }catch{/* Transcripts remain usable without an active review. */}
 };
 useEffect(()=>{
  mounted.current=true;refresh();
  const interval=setInterval(()=>{if(document.visibilityState!=='hidden')refresh();},25000);
  return()=>{mounted.current=false;clearInterval(interval);};
 },[projectId,sourceId,clientNotes]);
 const notes=(clientNotes||ownerData.notes).filter(n=>n.kind==='comment'&&n.source_id===sourceId&&n.status!=='dismissed');
 const replies=clientNotes?clientReplies||[]:ownerData.replies;
 const action=async(note,type)=>{
  if(busy)return false;
  setBusy(true);setError('');
  try{
   if(type==='reply'){
    const body=drafts[note.id]?.trim();if(!body)return false;
    if(clientNotes && onClientReply)await onClientReply(note,body);
    else if(note.private)await storyCall('reply',{id:crypto.randomUUID(),commentId:note.id,body});
    else await replyCall('reply',{id:crypto.randomUUID(),feedbackId:note.id,body});
    setDrafts(v=>({...v,[note.id]:''}));
   }else if(type==='resolve' && !clientNotes){
    if(note.private)await storyCall('resolve',{commentId:note.id});
    else await ownerCall('resolve',{feedbackId:note.id});
   }else if(type==='delete' && !clientNotes && note.private){
    await storyCall('delete',{commentId:note.id});
   }
   await refresh();
   return true;
  }catch(e){setError(e.message||'Could not update this comment.');return false;}
  finally{setBusy(false);}
 };
 const add=async(sourceId,first,last,body)=>{
   if(busy||!projectId||!sourceId)throw new Error('Open a saved interview to comment.');
   setBusy(true);setError('');
   try{await storyCall('addTranscript',{id:crypto.randomUUID(),sourceId,first,last,body});await refresh();}
   catch(e){setError(e.message);throw e;}
   finally{setBusy(false);}
 };
 return {notes,replies,drafts,setDrafts,action,add,busy,error,refresh};
}
